"use client";

import { useQueryStates } from "nuqs";
import React, { useTransition } from "react";
import { Checkbox } from "@/app/components/ui/checkbox";
import {
  clearTenantStandortResetCookie,
  setTenantStandortResetCookie,
  TENANT_STANDORT_RESET_PARAM,
} from "@/app/components/filters/tenant-location-reset";
import { searchParams } from "@/app/types/search-params";
import type { Category } from "@/app/types/category";
import type { arraySearchParams } from "@/app/types/search-params";

type SearchParamKey = keyof typeof arraySearchParams;
type QueryFilterStatesT = {
  marke: string[];
  standort: string[];
  abteilung: string[];
  kategorie: string[];
  tenantStandortReset: string;
};

export type QueryFilterProps = {
  categories: Record<string, Category>;
  /** Query key in the URL to bind to. Defaults to 'category'. */
  queryKey?: SearchParamKey;
  className?: string;
  /** If true (default), trigger server re-render on change (RSC aware). */
  notifyServer?: boolean;
  /** Number of columns to display options in (defaults to 1). */
  columns?: number;
};

export default function QueryFilter({
  categories,
  queryKey = "kategorie",
  className,
  notifyServer = true,
  columns = 1,
}: QueryFilterProps) {
  const [isPending, startTransition] = useTransition();

  const [queryStates, setQueryStates] = useQueryStates(
    {
      marke: searchParams.marke,
      standort: searchParams.standort,
      abteilung: searchParams.abteilung,
      kategorie: searchParams.kategorie,
      [TENANT_STANDORT_RESET_PARAM]: searchParams.tenantStandortReset,
    },
    {
      // When notifyServer is true, we want to re-render RSCs -> shallow must be false
      shallow: !notifyServer,
      startTransition,
    }
  );
  const selectedValues = queryStates[queryKey];

  const options = Object.values(categories).map((category) => ({
    label: category.name,
    value: category.id.toString(),
  }));

  const normalizedColumns =
    Number.isFinite(columns) && columns > 1
      ? Math.min(Math.floor(columns), 3)
      : 1;

  function splitIntoColumns<T>(items: T[], cols: number): T[][] {
    if (cols <= 1) return [items];
    const perCol = Math.ceil(items.length / cols);
    const result: T[][] = [];
    for (let i = 0; i < cols; i++) {
      const start = i * perCol;
      const end = start + perCol;
      const slice = items.slice(start, end);
      if (slice.length > 0) result.push(slice);
    }
    return result.length ? result : [items];
  }

  const optionColumns = splitIntoColumns(options, normalizedColumns);

  const handleToggle = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    const updates: Partial<QueryFilterStatesT> = {};

    switch (queryKey) {
      case "marke":
        updates.marke = next;
        break;
      case "standort":
        updates.standort = next;
        updates.tenantStandortReset = "";
        if (next.length === 0) {
          setTenantStandortResetCookie();
        } else {
          clearTenantStandortResetCookie();
        }
        break;
      case "abteilung":
        updates.abteilung = next;
        break;
      case "kategorie":
        updates.kategorie = next;
        break;
    }

    setQueryStates(updates);
  };

  return (
    <div className={className} data-pending={isPending ? "" : undefined}>
      <div
        className={
          normalizedColumns === 3
            ? "grid grid-cols-1 gap-4 md:grid-cols-3"
            : normalizedColumns === 2
              ? "grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-4"
              : "flex flex-col"
        }
      >
        {optionColumns.map((colOptions, idx) => (
          <div
            key={idx}
            className={
              normalizedColumns > 1
                ? "flex flex-col space-y-1"
                : "flex flex-col space-y-1"
            }
          >
            {colOptions.map((opt) => (
              <div
                key={opt.value}
                className="group inline-flex items-center gap-3 py-1 select-none"
              >
                <label
                  key={opt.value}
                  className="group inline-flex w-auto cursor-pointer items-center gap-3 select-none"
                >
                  <Checkbox
                    checked={selectedValues.includes(opt.value)}
                    onCheckedChange={() => handleToggle(opt.value)}
                    className="data-[state=checked]:border-primary size-5.5 rounded-[3px] border-2 border-[#5c5c5c] transition-colors group-hover:border-[#5c5c5c]"
                    aria-label={opt.label}
                  />
                  <span className="leading-none text-[#5c5c5c] group-hover:text-[#5c5c5c]">
                    {opt.label}
                  </span>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function QueryFilterSkeleton() {
  return (
    <div className="flex flex-col space-y-1">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-1">
          <div
            className="bg-muted/40 h-5.5 w-5.5 rounded-[3px] border-2 border-[#5c5c5c]"
            aria-hidden
          />
          <div
            className="bg-muted/60 h-4 w-52 rounded md:h-4 md:w-72"
            aria-hidden
          />
        </div>
      ))}
    </div>
  );
}
